import { getColorVariable } from "../../utils/helper";
import { type InputTextProps } from "./input-text";
import { KeyValueObject } from "../../types/general";

export function getBackgroundColor(
    outline: InputTextProps["outline"],
    underline: InputTextProps["underline"],
    color: InputTextProps["color"],
    clean: InputTextProps["clean"],
    theme: any,
): KeyValueObject | null {
    if (!clean) {
        if (outline || underline) {
            return null;
        }
        return {
            backgroundColor: getColorVariable(color, theme, {
                gray: theme.colors.gray[400],
                white: theme.colors.white,
                colorize: theme.colors[color || "primary"][500],
                default: theme.colors.gray[950],
            }),
        };
    }
    return null;
}

export function getOptionFontSize(size: InputTextProps["size"], theme: any): KeyValueObject {
    if (size === "sm") {
        return {
            fontSize: theme.fontSize["3xs"],
        };
    }
    if (size === "lg") {
        return {
            fontSize: theme.fontSize.sm,
        };
    }
    return {
        fontSize: theme.fontSize.xs,
    };
}

export function getFontSize(size: InputTextProps["size"], theme: any): KeyValueObject {
    if (size === "xs" || size === "sm") {
        return { fontSize: theme.fontSize["3xs"] };
    }
    return { fontSize: theme.fontSize.sm, [`@media (min-width: ${theme.screens.sm})`]: { fontSize: theme.fontSize.xs } };
}

export function getPaddingX(
    underline: InputTextProps["underline"],
    size: InputTextProps["size"],
    clean: InputTextProps["clean"],
    theme: any,
): KeyValueObject | null {
    if (!clean) {
        if (underline) {
            return { paddingLeft: "0", paddingRight: "0" };
        }
        if (size === "xs") {
            return { paddingLeft: theme.padding[1.5], paddingRight: theme.padding[1.5] };
        }
        if (size === "sm") {
            return { paddingLeft: theme.padding[2], paddingRight: theme.padding[2] };
        }
        if (size === "lg") {
            return {
                paddingLeft: theme.padding[3],
                paddingRight: theme.padding[3],
                [`@media (min-width: ${theme.screens.lg})`]: {
                    paddingRight: theme.padding[4],
                },
            };
        }
        return { paddingLeft: theme.padding[3], paddingRight: theme.padding[3] };
    }
    return null;
}

export function getPaddingY(underline: InputTextProps["underline"], size: InputTextProps["size"], theme: any): KeyValueObject {
    if ((underline && size === "lg") || size === "lg") {
        return {
            paddingTop: theme.padding[1.5],
            paddingBottom: theme.padding[1.5],
            [`@media (min-width: ${theme.screens.lg})`]: {
                paddingTop: theme.padding[3],
                paddingBottom: theme.padding[3],
            },
        };
    }
    if (size === "xs" || size === "sm") {
        return { paddingTop: "0", paddingBottom: "0" };
    }
    return { paddingTop: "3px", paddingBottom: "3px" };
}

export function getBorderRadius(rounded: InputTextProps["rounded"], theme: any): KeyValueObject | null {
    if (rounded === "md") {
        return { borderRadius: theme.borderRadius.DEFAULT };
    }
    if (rounded === "lg") {
        return { borderRadius: theme.borderRadius.lg };
    }
    if (rounded === "full") {
        return { borderRadius: theme.borderRadius.full };
    }
    if (rounded === "l-md") {
        return { borderTopLeftRadius: theme.borderRadius.DEFAULT, borderBottomLeftRadius: theme.borderRadius.DEFAULT };
    }
    if (rounded === "r-md") {
        return { borderTopRightRadius: theme.borderRadius.DEFAULT, borderBottomRightRadius: theme.borderRadius.DEFAULT };
    }
    if (rounded === "l-lg") {
        return { borderTopLeftRadius: theme.borderRadius.lg, borderBottomLeftRadius: theme.borderRadius.lg };
    }
    if (rounded === "r-lg") {
        return { borderTopRightRadius: theme.borderRadius.lg, borderBottomRightRadius: theme.borderRadius.lg };
    }
    if (rounded === "l-full") {
        return { borderTopLeftRadius: theme.borderRadius.full, borderBottomLeftRadius: theme.borderRadius.full };
    }
    if (rounded === "r-full") {
        return { borderTopRightRadius: theme.borderRadius.full, borderBottomRightRadius: theme.borderRadius.full };
    }
    return null;
}

export function getColor(
    outline: InputTextProps["outline"],
    underline: InputTextProps["underline"],
    outlineKeepOnHover: InputTextProps["outlineKeepOnHover"],
    color: InputTextProps["color"],
    clean: InputTextProps["clean"],
    theme: any,
): KeyValueObject {
    if (underline || (outline && outlineKeepOnHover) || clean) {
        return {
            color: getColorVariable(color, theme, {
                gray: theme.colors.gray[400],
                white: theme.colors.white,
                colorize: theme.colors[color || "primary"][500],
                default: theme.colors.gray[950],
            }),
        };
    }
    if (outline) {
        return {
            color: getColorVariable(color, theme, {
                gray: theme.colors.gray[400],
                white: theme.colors.white,
                colorize: theme.colors[color || "primary"][500],
                default: theme.colors.gray[950],
            }),
        };
    }
    return {
        color: getColorVariable(color, theme, {
            gray: theme.colors.white,
            white: theme.colors.gray[950],
            colorize: theme.colors.white,
            default: theme.colors.white,
        }),
    };
}

export function getIndicatorSeparatorBackgroundColor(
    outline: InputTextProps["outline"],
    underline: InputTextProps["underline"],
    outlineKeepOnHover: InputTextProps["outlineKeepOnHover"],
    color: InputTextProps["color"],
    clean: InputTextProps["clean"],
    theme: any,
): KeyValueObject {
    if (underline || (outline && outlineKeepOnHover) || clean) {
        return {
            backgroundColor: getColorVariable(color, theme, {
                gray: theme.colors.gray[400],
                white: theme.colors.white,
                colorize: theme.colors[color || "primary"][500],
                default: theme.colors.gray[950],
            }),
        };
    }
    if (outline) {
        return {
            backgroundColor: getColorVariable(color, theme, {
                gray: theme.colors.gray[400],
                white: theme.colors.white,
                colorize: theme.colors[color || "primary"][500],
                default: theme.colors.gray[950],
            }),
        };
    }
    return {
        backgroundColor: getColorVariable(color, theme, {
            gray: theme.colors.white,
            white: theme.colors.gray[950],
            colorize: theme.colors.white,
            default: theme.colors.white,
        }),
    };
}

export function getBorderColor(color: InputTextProps["color"], clean: InputTextProps["clean"], theme: any): KeyValueObject {
    if (clean) {
        return { borderColor: "transparent" };
    }
    return {
        borderColor: getColorVariable(color, theme, {
            gray: theme.colors.gray[400],
            white: theme.colors.white,
            colorize: theme.colors[color || "primary"][500],
            default: theme.colors.gray[950],
        }),
    };
}

export function getBoxShadow(
    shadow: InputTextProps["shadow"],
    color: InputTextProps["color"],
    error: InputTextProps["error"],
    theme: any,
): KeyValueObject | null {
    if (error) {
        return { boxShadow: theme.boxShadow["button-red-focus"] };
    }
    if (shadow) {
        const shadow = getColorVariable(color, theme, {
            gray: "button",
            white: "button",
            colorize: `button-${color}`,
            default: "button",
        });
        return {
            boxShadow: theme.boxShadow[shadow] || theme.boxShadow.DEFAULT,
        };
    }
    return null;
}

export function getBoxShadowFocus(
    color: InputTextProps["color"],
    underline: InputTextProps["underline"],
    clean: InputTextProps["clean"],
    error: InputTextProps["error"],
    theme: any,
): KeyValueObject | null {
    if (error) {
        return { boxShadow: theme.boxShadow["button-red-focus"] };
    }
    if (!underline && !clean) {
        const shadow = getColorVariable(color, theme, {
            gray: "button-focus",
            white: "button-focus",
            colorize: `button-${color}-focus`,
            default: "button-focus",
        });
        return {
            boxShadow: theme.boxShadow[shadow] || theme.boxShadow.DEFAULT,
        };
    }
    return null;
}

export function getMultiValueBackgroundColor(
    outline: InputTextProps["outline"],
    underline: InputTextProps["underline"],
    color: InputTextProps["color"],
    clean: InputTextProps["clean"],
    theme: any,
): KeyValueObject | null {
    if (!clean) {
        if (outline || underline) {
            return null;
        }
        return {
            backgroundColor: getColorVariable(color, theme, {
                gray: theme.colors.gray[200],
                white: theme.colors.gray[200],
                colorize: theme.colors[color || "primary"][200],
                default: theme.colors.gray[500],
            }),
        };
    }
    return null;
}
